import { useEffect, useRef, useState } from "react";
import { AiOutlineShoppingCart } from "react-icons/ai";
import { BsChevronDown } from "react-icons/bs";
import { useSelector } from "react-redux";
import { Link, matchPath, useLocation } from "react-router-dom";

import logo from "../../assets/Logo/kodevedic-logo-a-learning-platform-digital-learning-lms-platform.png";
import { NavbarLinks } from "../../data/navbar-links";
import { apiConnector } from "../../services/apiconnector";
import { categories } from "../../services/apis";
import { ACCOUNT_TYPE } from "../../utils/constants";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import ProfileDropdown from "../core/Auth/ProfileDropDown";
import MobileSidebarMenu from "./Navbar/MobileSidebarMenu";

function Navbar() {
  const { token } = useSelector((state) => state.auth);
  const { user }  = useSelector((state) => state.profile);
  const { totalItems } = useSelector((state) => state.cart);
  const location = useLocation();

  const [subLinks, setSubLinks]   = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [scrolled, setScrolled]   = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);

  const catalogRef = useRef(null);
  useOnClickOutside(catalogRef, () => setCatalogOpen(false));

  /* ── Fetch categories once ─────────────────────────── */
  useEffect(() => {
    (async () => {
      setCatLoading(true);
      try {
        const res = await apiConnector("GET", categories.CATEGORIES_API);
        setSubLinks(res.data.data ?? []);
      } catch (_) {}
      setCatLoading(false);
    })();
  }, []);

  /* ── Scroll-aware background ───────────────────────── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isHome = location.pathname === "/";

  const matchRoute = (route) => matchPath({ path: route }, location.pathname);

  /* ── Nav link active + hover classes ──────────────── */
  const linkCls = (path) =>
    `relative py-1 text-sm font-medium transition-colors duration-200 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:rounded-full after:bg-yellow-50 after:transition-all after:duration-200 ${
      matchRoute(path)
        ? "text-yellow-50 after:w-full"
        : "text-richblack-100 hover:text-richblack-5 after:w-0 hover:after:w-full"
    }`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-all duration-300 ${
        scrolled || !isHome
          ? "border-richblack-700 bg-richblack-900/95 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,8,0.4)]"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex w-11/12 max-w-maxContent items-center justify-between py-3">

        {/* ── Logo ────────────────────────────────────── */}
        <Link to="/" className="flex-shrink-0">
          <img
            src={logo}
            alt="KodeVedic"
            width={140}
            height={40}
            loading="eager"
            className="h-9 w-auto object-contain"
          />
        </Link>

        {/* ── Desktop nav ─────────────────────────────── */}
        <nav className="hidden md:flex items-center gap-7">
          {NavbarLinks.map((link) =>
            link.title === "Catalog" ? (
              /* Catalog dropdown */
              <div
                key="catalog"
                ref={catalogRef}
                className="relative"
              >
                <button
                  onClick={() => setCatalogOpen((v) => !v)}
                  className={`flex items-center gap-1 py-1 text-sm font-medium transition-colors duration-200 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:rounded-full after:bg-yellow-50 after:transition-all after:duration-200 ${
                    matchRoute("/catalog/:catalogName")
                      ? "text-yellow-50 after:w-full"
                      : "text-richblack-100 hover:text-richblack-5 after:w-0 hover:after:w-full"
                  }`}
                >
                  {link.title}
                  <BsChevronDown
                    className={`text-xs transition-transform duration-200 ${catalogOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {/* Dropdown panel */}
                <div
                  className={`absolute left-1/2 top-[calc(100%+12px)] z-50 w-56 -translate-x-1/2 rounded-xl border border-richblack-700 bg-richblack-800 shadow-[0_8px_32px_rgba(0,0,8,0.5)] transition-all duration-200 origin-top ${
                    catalogOpen
                      ? "scale-100 opacity-100 pointer-events-auto"
                      : "scale-95 opacity-0 pointer-events-none"
                  }`}
                >
                  {/* Arrow */}
                  <div className="absolute -top-[7px] left-1/2 -translate-x-1/2 h-3 w-3 rotate-45 rounded-sm border-l border-t border-richblack-700 bg-richblack-800" />

                  <div className="p-2">
                    {catLoading ? (
                      <p className="px-3 py-2 text-sm text-richblack-400">Loading…</p>
                    ) : subLinks.filter((s) => s?.courses?.length > 0).length ? (
                      subLinks
                        .filter((s) => s?.courses?.length > 0)
                        .map((sub) => (
                          <Link
                            key={sub._id}
                            to={`/catalog/${sub.name.split(" ").join("-").toLowerCase()}`}
                            onClick={() => setCatalogOpen(false)}
                            className="flex items-center rounded-lg px-3 py-2.5 text-sm text-richblack-100 hover:bg-richblack-700 hover:text-richblack-5 transition-colors duration-150"
                          >
                            {sub.name}
                          </Link>
                        ))
                    ) : (
                      <p className="px-3 py-2 text-sm text-richblack-400">No courses found</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <Link key={link.title} to={link.path} className={linkCls(link.path)}>
                {link.title}
              </Link>
            )
          )}
        </nav>

        {/* ── Desktop right actions ───────────────────── */}
        <div className="hidden md:flex items-center gap-3">
          {/* Cart - students only */}
          {user?.accountType === ACCOUNT_TYPE.STUDENT && (
            <Link to="/dashboard/cart" className="relative p-1.5 text-richblack-100 hover:text-richblack-5 transition-colors duration-200">
              <AiOutlineShoppingCart className="text-[22px]" />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-yellow-50 text-[9px] font-bold text-richblack-900">
                  {totalItems > 9 ? "9+" : totalItems}
                </span>
              )}
            </Link>
          )}

          {/* Auth */}
          {!token ? (
            <>
              <Link
                to="/login"
                className="rounded-lg border border-richblack-700 bg-transparent px-4 py-2 text-sm font-medium text-richblack-100 hover:border-richblack-500 hover:text-richblack-5 transition-all duration-200"
              >
                Log in
              </Link>
              <Link
                to="/signup"
                className="rounded-lg bg-yellow-50 px-4 py-2 text-sm font-bold text-richblack-900 hover:bg-yellow-25 transition-all duration-200 shadow-[0_0_12px_rgba(255,214,10,0.2)]"
              >
                Sign up
              </Link>
            </>
          ) : (
            <ProfileDropdown />
          )}
        </div>

        {/* ── Mobile hamburger ────────────────────────── */}
        <MobileSidebarMenu subLinks={subLinks} />
      </div>
    </header>
  );
}

export default Navbar;
